import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { fetchCollectionWithProducts, CollectionWithProducts, ShopifyProduct } from '@/services/shopify';
import { ProductCard } from '@/components/Shop/ProductCard';
import { CurrencySelector } from '@/components/Shop/CurrencySelector';
import { ShopBreadcrumb } from '@/components/Shop/ShopBreadcrumb';
import { ShopCategoryNav } from '@/components/Shop/ShopCategoryNav';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
const CollectionPage = () => {
  const {
    handle
  } = useParams<{
    handle: string;
  }>();
  const navigate = useNavigate();
  const [collection, setCollection] = useState<CollectionWithProducts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>('featured');
  const [filteredProducts, setFilteredProducts] = useState<ShopifyProduct[]>([]);
  useEffect(() => {
    const loadCollection = async () => {
      if (!handle) return;
      try {
        setIsLoading(true);
        const data = await fetchCollectionWithProducts(handle, 100);
        setCollection(data);
        if (data) {
          setFilteredProducts(data.products.edges);
        }
      } catch (error) {
        console.error('Error loading collection:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCollection();
  }, [handle]);
  useEffect(() => {
    if (!collection) return;
    let sorted = [...collection.products.edges];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => parseFloat(a.node.priceRange.minVariantPrice.amount) - parseFloat(b.node.priceRange.minVariantPrice.amount));
        break;
      case 'price-desc':
        sorted.sort((a, b) => parseFloat(b.node.priceRange.minVariantPrice.amount) - parseFloat(a.node.priceRange.minVariantPrice.amount));
        break;
      case 'title-asc':
        sorted.sort((a, b) => a.node.title.localeCompare(b.node.title));
        break;
      case 'title-desc':
        sorted.sort((a, b) => b.node.title.localeCompare(a.node.title));
        break;
      default:
        // featured - keep original order
        break;
    }
    setFilteredProducts(sorted);
  }, [sortBy, collection]);
  if (isLoading) {
    return <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>;
  }
  if (!collection) {
    return <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Collection not found</h2>
            <Button onClick={() => navigate('/shop')}>
              Back to Shop
            </Button>
          </div>
        </div>
      </Layout>;
  }
  return <Layout>
      <div className="min-h-screen pb-20">
        {/* Category Navigation */}
        <ShopCategoryNav />
        
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8 mt-4">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <ShopBreadcrumb items={[{
            label: 'Shop',
            href: '/shop'
          }, {
            label: collection.title
          }]} />
          </div>

          {/* Collection Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
              <div className="flex-1">
                <div className="gap-4 mb-4 flex-row flex items-center justify-start">
                  {collection.image && <div className="w-11 h-11 rounded-lg overflow-hidden bg-secondary/10 flex-shrink-0">
                      <img src={collection.image.url} alt={collection.image.altText || collection.title} className="w-full h-full object-cover" />
                    </div>}
                  <h2 className="text-4xl md:text-4xl font-normal">{collection.title}</h2>
                </div>
                {collection.description && <p className="text-foreground max-w-3xl text-sm leading-relaxed">
                    {collection.description}
                  </p>}
              </div>
            </div>

            {/* Controls Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b">
              <p className="text-muted-foreground">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
              </p>
              
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <CurrencySelector />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="price-asc">Price: Low to High</SelectItem>
                    <SelectItem value="price-desc">Price: High to Low</SelectItem>
                    <SelectItem value="title-asc">Name: A to Z</SelectItem>
                    <SelectItem value="title-desc">Name: Z to A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredProducts.map(product => <ProductCard key={product.node.id} product={product} />)}
            </div> : <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">
                No products found in this collection.
              </p>
            </div>}
        </div>
      </div>
    </Layout>;
};
export default CollectionPage;